/*
 * AMG Barcode — Code 128 (subset B) renderer.
 *
 * Draws the barcode as inline SVG rather than fetching an image from a
 * third-party service, so printing never depends on an outside host being up
 * and the bars stay sharp at whatever DPI the printer runs at. Inline SVG also
 * survives being copied into the print window as markup.
 *
 * Used by sections/order-slip.liquid, which calls render() after building the
 * slip HTML. Mirrors src/lib/barcode.ts in customer-accounts-dashboard; keep
 * the two in sync so both packing slips scan identically.
 *
 * Usage:
 *   <div data-amg-barcode="#1001"></div>   → filled in automatically on load
 *   AMGBarcode.render(container)           → fill in placeholders under a node
 *   AMGBarcode.svg('#1001')                → get the SVG markup as a string
 */
(function () {
  'use strict';

  // Bar/space widths for Code 128 values 0-106. Each digit is the width, in
  // modules, of one element; elements alternate bar, space, bar, ... The last
  // three entries are START A, START B, START C and STOP (values 103-106).
  var PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
    '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
    '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
    '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
    '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
    '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
    '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
    '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
    '114131', '311141', '411131', '211412', '211214', '211232', '2331112'
  ];

  var START_B = 104;
  var STOP = 106;

  // Subset B covers ASCII 32-126, where the Code 128 value is the code point
  // minus 32. Anything outside that range can't be encoded, so drop it.
  function toValues(text) {
    var values = [];
    for (var i = 0; i < text.length; i++) {
      var code = text.charCodeAt(i);
      if (code >= 32 && code <= 126) values.push(code - 32);
    }
    return values;
  }

  function checksum(values) {
    var sum = START_B;
    for (var i = 0; i < values.length; i++) sum += values[i] * (i + 1);
    return sum % 103;
  }

  /**
   * Build the barcode as an SVG string.
   *
   * The SVG carries a viewBox in module units and no intrinsic width, so CSS
   * decides how wide it prints — the module widths scale with it.
   */
  function svg(text, options) {
    var opts = options || {};
    var height = opts.height || 40;
    var quietZone = opts.quietZone == null ? 10 : opts.quietZone;

    var values = toValues(String(text == null ? '' : text));
    if (!values.length) return '';

    var sequence = [START_B].concat(values, [checksum(values)], [STOP]);

    var rects = '';
    var x = quietZone;
    for (var i = 0; i < sequence.length; i++) {
      var pattern = PATTERNS[sequence[i]];
      for (var j = 0; j < pattern.length; j++) {
        var width = parseInt(pattern.charAt(j), 10);
        // Even positions are bars, odd positions are spaces.
        if (j % 2 === 0) {
          rects += '<rect x="' + x + '" y="0" width="' + width + '" height="' + height + '"/>';
        }
        x += width;
      }
    }

    var totalWidth = x + quietZone;

    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalWidth + ' ' + height + '"' +
      ' preserveAspectRatio="none" shape-rendering="crispEdges" fill="#000"' +
      ' role="img" aria-label="Barcode: ' + escapeAttribute(text) + '">' +
      rects +
      '</svg>'
    );
  }

  function escapeAttribute(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** Fill every [data-amg-barcode] placeholder under root with its barcode. */
  function render(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-amg-barcode]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var value = node.getAttribute('data-amg-barcode');
      if (!value) continue;
      node.innerHTML = svg(value, {
        height: parseFloat(node.getAttribute('data-amg-barcode-height')) || undefined
      });
    }
  }

  window.AMGBarcode = { svg: svg, render: render };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { render(); });
  } else {
    render();
  }
})();
